import type { FunctionComponent, PropsWithChildren } from 'react';
import { z } from 'zod';

import {
    createJsaProfileDetailTilePayload,
    createWorkTaskTilePayload,
    type JobProfileTilePanels,
    JobProfileTilePanelsProvider,
} from '../job-architecture-v2';
import {
    createUrlTileCodec,
    createUrlTileDefinition,
    type UrlTileModule,
    type UrlTileRenderContext,
    UrlTileSubjectActionsProvider,
    type UrlTileSubjectActionsRenderer,
} from '../url-tiles';
import { PaletteProvider, usePaletteFromUrl } from './components/PaletteProvider';
import { ItemTile } from './item-tile/ItemTile';
import { TilePriorityButton } from './strategy/components/TilePriorityButton';
import { RoleTilePositions, RoleTileTasks } from './strategy/role-tile/RoleTilePanels';
import { RoleTileWithoutJobArchitecture } from './strategy/role-tile/RoleTileWithoutJobArchitecture';

export const AI_TRANSFORMATION_ITEM_TILE_TYPE = 'v1.ai-transformation.item' as const;
export const AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY = 'ai-transformation.item';
const ITEM_TILE_WIDTH = '520px';

const idSchema = z
    .string()
    .trim()
    .min(1)
    .refine((value) => !value.includes('.'));

export const aiTransformationItemTileSchema = z.object({
    architectureId: idSchema,
    itemId: idSchema,
    type: z.literal(AI_TRANSFORMATION_ITEM_TILE_TYPE),
});

export type AiTransformationItemTilePayload = z.infer<typeof aiTransformationItemTileSchema>;

export const createAiTransformationItemTilePayload = (
    architectureId: string,
    itemId: string,
): AiTransformationItemTilePayload => ({
    architectureId,
    itemId,
    type: AI_TRANSFORMATION_ITEM_TILE_TYPE,
});

type ItemTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AiTransformationItemTilePayload;
};

const ItemTileWithPalette: FunctionComponent<ItemTileDefinitionProps> = ({ context, payload }) => {
    const palette = usePaletteFromUrl();

    return (
        <PaletteProvider palette={palette}>
            <ItemTile
                architectureId={payload.architectureId}
                itemId={payload.itemId}
                onClose={() => context.closeTile(AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY)}
                onOpenJsaItem={({ itemId, layer }) =>
                    context.openTile(
                        createJsaProfileDetailTilePayload({
                            architectureId: payload.architectureId,
                            itemType: layer === 'level' ? 'job-level' : 'job-role',
                            mode: 'view',
                            profileId: itemId,
                        }),
                    )
                }
                onOpenJsaTask={(taskId) => context.openTile(createWorkTaskTilePayload({ taskId }))}
            />
        </PaletteProvider>
    );
};

export const aiTransformationItemUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<AiTransformationItemTilePayload>({
        options: [],
        segments: ['architectureId', 'itemId'],
        type: AI_TRANSFORMATION_ITEM_TILE_TYPE,
    }),
    maxWidth: ITEM_TILE_WIDTH,
    minWidth: ITEM_TILE_WIDTH,
    render: (props: ItemTileDefinitionProps) => <ItemTileWithPalette {...props} />,
    schema: aiTransformationItemTileSchema,
    slotKey: () => AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY,
    tileKey: ({ architectureId, itemId }) => `${AI_TRANSFORMATION_ITEM_TILE_SLOT_KEY}.${architectureId}.${itemId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: ITEM_TILE_WIDTH,
});

const roleTilePanels: JobProfileTilePanels = {
    Positions: RoleTilePositions,
    Tasks: RoleTileTasks,
    WithoutJobArchitecture: RoleTileWithoutJobArchitecture,
};

const renderSubjectActions: UrlTileSubjectActionsRenderer = (subject) => <TilePriorityButton subject={subject} />;

/**
 * Gives the role and level tiles their Tasks and Positions tabs, read from the positions, and puts the priority button
 * into the header of every position, task, role and level tile.
 */
const AiTransformationUrlTilesProvider: FunctionComponent<PropsWithChildren> = ({ children }) => (
    <JobProfileTilePanelsProvider panels={roleTilePanels}>
        <UrlTileSubjectActionsProvider value={renderSubjectActions}>{children}</UrlTileSubjectActionsProvider>
    </JobProfileTilePanelsProvider>
);

export const aiTransformationUrlTileModule: UrlTileModule = {
    definitions: [aiTransformationItemUrlTileDefinition],
    Provider: AiTransformationUrlTilesProvider,
};
